/**
 * SERVA — briques d'interface. « Cloud / Soft Minimal ».
 * Rayons : bouton 10, carte 14, feuille 20, badge 999. Jamais le même partout.
 * Deux niveaux d'ombre. Transitions sur transform et opacity uniquement.
 */
import React, { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "./ui/drawer";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Skeleton } from "./ui/skeleton";

/* ------------------------------------------------------------------ hooks */

export function useBureau() {
  const [bureau, setBureau] = useState(
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const on = (e) => setBureau(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return bureau;
}

/* ---------------------------------------------------------------- basiques */

export function Micro({ children, muted = false, className = "", ...rest }) {
  return (
    <div className={`micro ${muted ? "micro-muted" : ""} ${className}`} {...rest}>
      {children}
    </div>
  );
}

const VARIANTES = {
  primaire:
    "bg-[var(--accent)] text-white border border-transparent hover:bg-[var(--accent-hover)]",
  secondaire:
    "bg-[var(--accent-soft)] text-[var(--accent)] border border-transparent hover:bg-[#dde5e0]",
  fantome:
    "bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] hover:border-[var(--border-strong)]",
  nu: "bg-transparent text-[var(--text-muted)] border border-transparent hover:text-[var(--text)] hover:bg-[rgba(20,20,19,0.04)]",
  danger:
    "bg-transparent text-[var(--danger)] border border-[rgba(158,65,50,0.30)] hover:bg-[var(--risk-high-bg)]",
  olive:
    "bg-transparent text-[var(--success)] border border-[rgba(46,107,82,0.30)] hover:bg-[var(--risk-low-bg)]",
};

const TAILLES = {
  sm: "h-9 px-3.5 text-[13px]",
  md: "h-11 px-4 text-[14px]",
  lg: "h-12 px-5 text-[15px]",
};

export function Btn({
  children, variante = "primaire", taille = "md", charge = false,
  className = "", type = "button", icone: Icone, ...rest
}) {
  return (
    <button
      type={type}
      disabled={rest.disabled || charge}
      className={`inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap
        ${VARIANTES[variante]} ${TAILLES[taille]}
        disabled:opacity-45 disabled:cursor-not-allowed
        active:scale-[0.98] ${className}`}
      style={{
        borderRadius: "var(--radius-md)",
        transition:
          "background-color var(--dur) var(--ease), border-color var(--dur) var(--ease), color var(--dur) var(--ease), transform var(--dur-micro) var(--ease)",
      }}
      {...rest}
    >
      {charge ? (
        <Loader2 size={15} strokeWidth={1.9} className="animate-spin" />
      ) : Icone ? (
        <Icone size={16} strokeWidth={1.8} />
      ) : null}
      {children}
    </button>
  );
}

export const CHAMP_BASE =
  "w-full bg-[var(--surface)] text-[15px] text-[var(--text)] border outline-none placeholder:text-[var(--text-subtle)] focus:border-[var(--accent)]";

export function Champ({ label, indice, erreur, className = "", suffixe, ...rest }) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="block text-[13px] font-semibold text-[var(--text-muted)] mb-1.5">
          {label}
        </span>
      )}
      <span className="relative block">
        <input
          className={`${CHAMP_BASE} h-11 px-3.5 ${
            erreur ? "border-[var(--danger)]" : "border-[var(--border)]"
          } ${suffixe ? "pr-12" : ""}`}
          style={{
            borderRadius: "var(--radius-md)",
            transition: "border-color var(--dur) var(--ease)",
          }}
          {...rest}
        />
        {suffixe && (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[13px] num text-[var(--text-subtle)]">
            {suffixe}
          </span>
        )}
      </span>
      {erreur ? (
        <span className="block text-[12px] text-[var(--danger)] mt-1.5">{erreur}</span>
      ) : indice ? (
        <span className="block text-[12px] text-[var(--text-subtle)] mt-1.5">{indice}</span>
      ) : null}
    </label>
  );
}

/** Champ adresse avec suggestions en direct (API Adresse — data.gouv.fr, gratuite, sans clé).
 * Le libellé choisi contient déjà le code postal + la ville, exploités ensuite pour la zone
 * publique (masquage de l'adresse exacte tant que le rendez-vous n'est pas confirmé). */
export function AdresseAutocomplete({ label, indice, value, onChange, testid = "champ-adresse", placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [ouvert, setOuvert] = useState(false);
  const minuteur = useRef(null);

  const chercher = (texte) => {
    onChange(texte);
    clearTimeout(minuteur.current);
    if (texte.trim().length < 4) {
      setSuggestions([]);
      return;
    }
    minuteur.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(texte)}&limit=5`
        );
        const d = await r.json();
        setSuggestions(d.features || []);
        setOuvert(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
  };

  const choisir = (f) => {
    onChange(f.properties.label);
    setSuggestions([]);
    setOuvert(false);
  };

  return (
    <label className="block relative">
      {label && (
        <span className="block text-[13px] font-semibold text-[var(--text-muted)] mb-1.5">
          {label}
        </span>
      )}
      <input
        className={`${CHAMP_BASE} h-11 px-3.5 border-[var(--border)]`}
        style={{ borderRadius: "var(--radius-md)", transition: "border-color var(--dur) var(--ease)" }}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => chercher(e.target.value)}
        onFocus={() => suggestions.length && setOuvert(true)}
        onBlur={() => setTimeout(() => setOuvert(false), 150)}
        data-testid={testid}
      />
      {ouvert && suggestions.length > 0 && (
        <ul
          className="absolute left-0 right-0 mt-1 py-1 max-h-56 overflow-auto z-20"
          style={{
            background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-md)",
          }}
        >
          {suggestions.map((f) => (
            <li
              key={f.properties.id}
              onMouseDown={() => choisir(f)}
              className="px-3.5 py-2 text-[14px] cursor-pointer hover:bg-[var(--bg)]"
              data-testid="adresse-suggestion"
            >
              {f.properties.label}
            </li>
          ))}
        </ul>
      )}
      {indice && <span className="block text-[12px] text-[var(--text-subtle)] mt-1.5">{indice}</span>}
    </label>
  );
}


export function Zone({ label, indice, className = "", ...rest }) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="block text-[13px] font-semibold text-[var(--text-muted)] mb-1.5">
          {label}
        </span>
      )}
      <textarea
        className={`${CHAMP_BASE} min-h-[96px] p-3.5 leading-relaxed resize-y border-[var(--border)]`}
        style={{ borderRadius: "var(--radius-md)", transition: "border-color var(--dur) var(--ease)" }}
        {...rest}
      />
      {indice && <span className="block text-[12px] text-[var(--text-subtle)] mt-1.5">{indice}</span>}
    </label>
  );
}

export function Choix({ label, valeur, surChangement, options, placeholder = "Choisir", testid }) {
  return (
    <label className="block">
      {label && (
        <span className="block text-[13px] font-semibold text-[var(--text-muted)] mb-1.5">
          {label}
        </span>
      )}
      <Select value={valeur || undefined} onValueChange={surChangement}>
        <SelectTrigger
          data-testid={testid}
          className="h-11 border-[var(--border)] bg-[var(--surface)] text-[15px] text-[var(--text)]"
          style={{ borderRadius: "var(--radius-md)" }}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent
          className="border-[var(--border)] bg-[var(--surface)]"
          style={{ borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-md)" }}
        >
          {options.map((o) => (
            <SelectItem
              key={o.valeur}
              value={o.valeur}
              className="text-[15px] py-2.5"
              data-testid={testid ? `${testid}-${o.valeur}` : undefined}
            >
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

export function Bascule({ label, description, actif, surChangement, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3.5">
      <div className="min-w-0">
        <div className="text-[15px] font-semibold">{label}</div>
        {description && (
          <div className="text-[13px] text-[var(--text-muted)] mt-0.5">{description}</div>
        )}
      </div>
      <Switch checked={!!actif} onCheckedChange={surChangement} data-testid={testid} className="mt-1" />
    </div>
  );
}

/* ----------------------------------------------------------------- entetes */

export function EnTete({ micro, titre, sous, actions, className = "" }) {
  return (
    <div className={`flex items-end justify-between gap-4 ${className}`}>
      <div className="min-w-0">
        {micro && <Micro className="mb-1.5">{micro}</Micro>}
        <h1 className="display text-[24px] md:text-[28px] leading-[1.1]">{titre}</h1>
        {sous && <p className="text-[13px] text-[var(--text-muted)] mt-1.5">{sous}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ badges */

const TONS = {
  neutre: "text-[var(--text-muted)] bg-[var(--surface-sunken)]",
  olive: "text-[var(--risk-low-fg)] bg-[var(--risk-low-bg)]",
  accent: "text-[var(--accent)] bg-[var(--accent-soft)]",
  ambre: "text-[var(--risk-mid-fg)] bg-[var(--risk-mid-bg)]",
  brique: "text-[var(--risk-high-fg)] bg-[var(--risk-high-bg)]",
};

export function Pastille({ ton = "neutre", children, ...rest }) {
  return (
    <span
      className={`inline-flex items-center h-[21px] px-2.5 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.09em] whitespace-nowrap ${TONS[ton]}`}
      {...rest}
    >
      {children}
    </span>
  );
}

export function ScoreRisque({ risque, compact = false }) {
  if (!risque) return null;
  const ton = { faible: "olive", modere: "ambre", eleve: "brique" }[risque.niveau] || "neutre";
  const mot = { faible: "Fiable", modere: "À confirmer", eleve: "Risque élevé" }[risque.niveau];
  if (compact) {
    return (
      <Pastille ton={ton} data-testid="badge-risque">
        {mot}
      </Pastille>
    );
  }
  return (
    <div className="surface p-4" data-testid="bloc-risque">
      <div className="flex items-baseline justify-between gap-4">
        <Micro>Risque d'absence</Micro>
        <div className="flex items-baseline gap-1.5">
          <span className="num text-[26px] leading-none" data-testid="score-risque">
            {risque.score}
          </span>
          <span className="text-[12px] text-[var(--text-subtle)]">/100</span>
        </div>
      </div>
      <div className="barre mt-3">
        <span
          style={{
            width: `${risque.score}%`,
            background:
              risque.niveau === "eleve"
                ? "var(--danger)"
                : risque.niveau === "modere"
                ? "var(--warning)"
                : "var(--success)",
          }}
        />
      </div>
      {risque.facteurs?.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {risque.facteurs.map((f, i) => (
            <li key={i} className="text-[13px] text-[var(--text-muted)] flex gap-2">
              <span className="text-[var(--text-subtle)]">—</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
      {risque.conseil && (
        <p className="text-[13px] mt-3 pt-3 border-t border-[var(--border)]">{risque.conseil}</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- data */

export function Stat({ micro, valeur, unite, note, testid, accent = false }) {
  return (
    <div className="surface p-4" data-testid={testid}>
      <Micro muted={!accent}>{micro}</Micro>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="num text-[26px] md:text-[30px] leading-none">{valeur}</span>
        {unite && <span className="text-[13px] text-[var(--text-subtle)]">{unite}</span>}
      </div>
      {note && <div className="text-[12px] text-[var(--text-muted)] mt-2">{note}</div>}
    </div>
  );
}

export function Barre({ pct, ton = "accent" }) {
  const couleur = {
    accent: "var(--accent)",
    olive: "var(--success)",
    ambre: "var(--warning)",
  }[ton];
  return (
    <div className="barre">
      <span style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: couleur }} />
    </div>
  );
}

/* ------------------------------------------------------------------ états */

export function Vide({ titre, texte, action, icone: Icone }) {
  return (
    <div className="py-12 px-6 text-center" data-testid="etat-vide">
      {Icone && (
        <span
          className="mx-auto mb-4 inline-flex h-11 w-11 items-center justify-center"
          style={{
            borderRadius: "var(--radius-full)",
            background: "var(--accent-soft)",
            color: "var(--accent)",
          }}
        >
          <Icone size={20} strokeWidth={1.7} />
        </span>
      )}
      <h3 className="display text-[20px]">{titre}</h3>
      {texte && (
        <p className="text-[14px] text-[var(--text-muted)] mt-2 max-w-[36ch] mx-auto leading-relaxed">
          {texte}
        </p>
      )}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

export function Squelette({ lignes = 3 }) {
  return (
    <div className="space-y-3" data-testid="squelette">
      {Array.from({ length: lignes }).map((_, i) => (
        <div key={i} className="surface p-4">
          <Skeleton className="h-3 w-24 mb-3 bg-[var(--surface-sunken)]" />
          <Skeleton className="h-4 w-2/3 mb-2 bg-[var(--surface-sunken)]" />
          <Skeleton className="h-3 w-1/3 bg-[var(--surface-sunken)]" />
        </div>
      ))}
    </div>
  );
}

export function Erreur({ message, surReessai }) {
  return (
    <div className="surface p-5" data-testid="etat-erreur">
      <Micro className="text-[var(--danger)]">Ça n'a pas marché</Micro>
      <p className="text-[14px] mt-2">{message}</p>
      {surReessai && (
        <Btn variante="fantome" taille="sm" className="mt-4" onClick={surReessai} data-testid="btn-reessayer">
          Réessayer
        </Btn>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- overlays */

/** Feuille glissante sur mobile, boîte de dialogue sur ordinateur. */
export function Feuille({ ouvert, surFermeture, titre, micro, children, pied, testid }) {
  const bureau = useBureau();
  const entete = (
    <div className="px-5 pt-5 pb-4 border-b border-[var(--border)]">
      {micro && <Micro className="mb-1.5">{micro}</Micro>}
      <div className="display text-[21px] pr-8">{titre}</div>
    </div>
  );
  const fermer = (
    <button
      onClick={surFermeture}
      aria-label="Fermer"
      data-testid="feuille-fermer"
      className="absolute right-3.5 top-4 h-9 w-9 inline-flex items-center justify-center text-[var(--text-subtle)] hover:text-[var(--text)] hover:bg-[rgba(20,20,19,0.05)]"
      style={{ borderRadius: "var(--radius-md)" }}
    >
      <X size={17} strokeWidth={1.8} />
    </button>
  );

  if (bureau) {
    return (
      <Dialog open={ouvert} onOpenChange={(v) => !v && surFermeture()}>
        <DialogContent
          data-testid={testid}
          className="p-0 gap-0 max-w-[560px] border-[var(--glass-border)] overflow-hidden"
          style={{
            borderRadius: "var(--radius-xl)",
            background: "var(--glass-panel)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <DialogTitle className="sr-only">{titre}</DialogTitle>
          {entete}
          {fermer}
          <div className="px-5 py-5 max-h-[62vh] overflow-y-auto">{children}</div>
          {pied && (
            <div className="px-5 py-4 border-t border-[var(--border)] flex items-center justify-end gap-2">
              {pied}
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Drawer open={ouvert} onOpenChange={(v) => !v && surFermeture()}>
      <DrawerContent
        data-testid={testid}
        className="border-[var(--glass-border)] max-h-[92vh]"
        style={{
          background: "var(--glass-panel)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderTopLeftRadius: "var(--radius-xl)",
          borderTopRightRadius: "var(--radius-xl)",
        }}
      >
        <DrawerTitle className="sr-only">{titre}</DrawerTitle>
        {entete}
        {fermer}
        <div className="px-5 py-5 overflow-y-auto">{children}</div>
        {pied && (
          <div
            className="px-5 py-4 border-t border-[var(--border)] flex items-center gap-2"
            style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom))" }}
          >
            {pied}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}

/** Ligne de liste : filets fins, pas de cartes empilées. */
export function Ligne({ children, onClick, testid, className = "" }) {
  const Balise = onClick ? "button" : "div";
  return (
    <Balise
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left flex items-center gap-3 py-3.5 border-b border-[var(--border)]
        ${onClick ? "hover:bg-[rgba(20,20,19,0.02)]" : ""} ${className}`}
      style={{ transition: "background-color var(--dur) var(--ease)" }}
    >
      {children}
    </Balise>
  );
}

export function Avatar({ initiales, ton = "neutre", taille = 40 }) {
  return (
    <span
      className={`shrink-0 inline-flex items-center justify-center font-semibold ${TONS[ton]}`}
      style={{
        height: taille,
        width: taille,
        borderRadius: "var(--radius-full)",
        fontSize: taille > 44 ? 16 : 13,
      }}
    >
      {initiales}
    </span>
  );
}
